import { useSearchParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { useUpdateErefConfiguration } from "hooks/api/quotation/quotations.hook"
import { useApiErrorFormatter } from "hooks/apiErrorFormatter.hook"
import { useEntityMutationQueue } from "hooks/mutationQueue.hook"
import { erefQueueKey } from "components/ui/customWebviewer/ReferenceSidebar/erefQueueKey"
import type { AxiosResponse } from "axios"
import type {
	TErefEstimationConfig,
	TErefWithFurniture,
	TFurniture,
	TUpdateErefConfigurationRequest,
	TUpdateErefConfigurationResponse,
} from "types/elevationReferences.type"
import type { TQuotationDetail } from "types/quotations.type"

export type TErefConfigurationSavePayload = Omit<TUpdateErefConfigurationRequest, "version">

// Optimistic cache patch in the *stored* estimation_config shape. The request sends
// delivery_date_id (a number), but the cached/GET shape stores delivery_date as an
// object, so the delivery-date caller passes an explicit patch here.
export type TErefConfigOptimisticPatch = Partial<Omit<TErefEstimationConfig, "eref_id">>

type QuotationCache = AxiosResponse<TQuotationDetail>

const patchErefInCache = (
	cache: QuotationCache | undefined,
	erefId: number,
	patch: (e: TQuotationDetail["related_objects"][number]["erefs"][number]) => TQuotationDetail["related_objects"][number]["erefs"][number],
): QuotationCache | undefined => {
	if (!cache) return cache
	const pageIdx = cache.data.related_objects.findIndex((p) => p.erefs.some((e) => e.id === erefId))
	if (pageIdx === -1) return cache
	const pages = [...cache.data.related_objects]
	const page = pages[pageIdx]
	pages[pageIdx] = {
		...page,
		erefs: page.erefs.map((e) => (e.id === erefId ? patch(e) : e)),
	}
	return { ...cache, data: { ...cache.data, related_objects: pages } }
}

const replaceFurnitureInCache = (
	cache: QuotationCache | undefined,
	updatedList: TFurniture[],
): QuotationCache | undefined => {
	if (!cache || updatedList.length === 0) return cache
	const byId = new Map(updatedList.map((f) => [f.id, f]))
	let anyChanged = false
	const pages = cache.data.related_objects.map((page) => {
		let pageChanged = false
		const nextFurniture = page.furniture.map((f) => {
			const replacement = byId.get(f.id)
			if (!replacement) return f
			pageChanged = true
			return replacement
		})
		if (!pageChanged) return page
		anyChanged = true
		return { ...page, furniture: nextFurniture }
	})
	if (!anyChanged) return cache
	return { ...cache, data: { ...cache.data, related_objects: pages } }
}

export const useErefConfigurationSave = (eref: TErefWithFurniture) => {
	const [searchParams] = useSearchParams()
	const quotationId = Number(searchParams.get("quotationID"))
	const queryClient = useQueryClient()
	const { formatError } = useApiErrorFormatter()
	const { mutateAsync } = useUpdateErefConfiguration()
	const { enqueue, versionRef, isPending } = useEntityMutationQueue(erefQueueKey(eref.id), eref.version)

	const save = (payload: TErefConfigurationSavePayload, optimistic?: TErefConfigOptimisticPatch) => {
		const queryKey = ["quotation", quotationId]
		const snapshot = queryClient.getQueryData<QuotationCache>(queryKey)

		// For most fields the request key matches the stored key, so the payload doubles
		// as the optimistic patch. delivery_date_id is the exception: it's a request-only
		// key (the cache stores a delivery_date object), so its caller passes an explicit
		// object-shaped patch. When no patch is given, drop delivery_date_id from the copy
		// so it can never leak into the cached estimation_config.
		let optimisticPatch: TErefConfigOptimisticPatch
		if (optimistic) {
			optimisticPatch = optimistic
		} else {
			const copy = { ...payload } as TErefConfigOptimisticPatch & { delivery_date_id?: number | null }
			delete copy.delivery_date_id
			optimisticPatch = copy
		}

		queryClient.setQueryData<QuotationCache>(queryKey, (prev) =>
			patchErefInCache(prev, eref.id, (e) => ({
				...e,
				estimation_config: {
					...(e.estimation_config ?? { eref_id: e.id }),
					...optimisticPatch,
				} as TErefEstimationConfig,
			})),
		)

		enqueue(async () => {
			try {
				const response = await mutateAsync({
					quotationId,
					erefId: eref.id,
					body: { version: versionRef.current, ...payload },
				})
				const { version, configuration, updated_furniture_list } =
					response.data as TUpdateErefConfigurationResponse
				versionRef.current = version
				queryClient.setQueryData<QuotationCache>(queryKey, (prev) => {
					const withEref = patchErefInCache(prev, eref.id, (e) => ({
						...e,
						version,
						estimation_config: {
							...(e.estimation_config ?? { eref_id: e.id }),
							...configuration,
						} as TErefEstimationConfig,
					}))
					return replaceFurnitureInCache(withEref, updated_furniture_list ?? [])
				})
			} catch (err) {
				queryClient.setQueryData(queryKey, snapshot)
				formatError(err)
			}
		})
	}

	return { save, isPending }
}
